import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Clock, 
  Target, 
  Award,
  AlertCircle,
  CheckCircle,
  Loader2,
  HelpCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { ScrollArea } from '../../ui/scroll-area';
import { useToast } from '../../../hooks/useToast';
import { useTenantContext } from '../../../context/TenantContext';
import { getQuizStatistics, getQuizQuestionsStatistics, formatQuizTime } from '../../../services/elearningQuizService';
import { StatisticsExport } from './StatisticsExport';

interface QuizStatisticsProps {
  quizId: string;
}

const QuizStatistics: React.FC<QuizStatisticsProps> = ({ quizId }) => {
  const { tenant } = useTenantContext();
  const { toast } = useToast();

  const [statistics, setStatistics] = useState<any>(null);
  const [questionStatistics, setQuestionStatistics] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);

  useEffect(() => {
    loadStatistics();
  }, [quizId, tenant?.id]);

  const loadStatistics = async () => {
    if (!tenant?.id) return;

    setIsLoading(true);
    try {
      const stats = await getQuizStatistics(quizId, tenant.id);
      setStatistics(stats);
      
      // Charger aussi les statistiques par question
      await loadQuestionStatistics();
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les statistiques',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadQuestionStatistics = async () => {
    if (!tenant?.id) return;

    setIsLoadingQuestions(true);
    try {
      const questionStats = await getQuizQuestionsStatistics(quizId, tenant.id);
      setQuestionStatistics(questionStats);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques par question:', error);
      // Ne pas afficher d'erreur pour les statistiques par question car c'est une fonctionnalité additionnelle
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"
        />
        <span className="ml-3 text-gray-400">Chargement des statistiques...</span>
      </div>
    );
  }

  if (!statistics || statistics.totalAttempts === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">Aucune donnée disponible</h3>
          <p className="text-gray-400">Les statistiques apparaîtront une fois que les étudiants auront commencé à passer le quiz</p>
        </div>
      </div>
    );
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreBadgeColor = (score: number) => {
    if (score >= 80) return 'border-green-500 text-green-400';
    if (score >= 60) return 'border-yellow-500 text-yellow-400';
    return 'border-red-500 text-red-400';
  };

  return (
    <ScrollArea className="h-full p-6">
      <div className="space-y-6">
        {/* Métriques principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border-blue-500/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-400 text-sm font-medium">Total Tentatives</p>
                    <p className="text-2xl font-bold text-white">{statistics.totalAttempts}</p>
                  </div>
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-gradient-to-br from-green-500/10 to-green-600/10 border-green-500/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-400 text-sm font-medium">Taux de Réussite</p>
                    <p className="text-2xl font-bold text-white">{Math.round(statistics.passRate)}%</p>
                  </div>
                  <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 border-purple-500/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-400 text-sm font-medium">Score Moyen</p>
                    <p className={`text-2xl font-bold ${getScoreColor(statistics.averageScore)}`}>
                      {statistics.averageScore}%
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <Target className="w-5 h-5 text-purple-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/10 border-orange-500/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-400 text-sm font-medium">Temps Moyen</p>
                    <p className="text-2xl font-bold text-white">{formatQuizTime(statistics.averageTime)}</p>
                  </div>
                  <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-orange-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Graphique de distribution des scores */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-blue-400" />
                Distribution des Scores
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { range: '90-100%', count: statistics.attempts.filter((a: any) => a.percentage >= 90).length, color: 'bg-green-500' },
                  { range: '80-89%', count: statistics.attempts.filter((a: any) => a.percentage >= 80 && a.percentage < 90).length, color: 'bg-blue-500' },
                  { range: '70-79%', count: statistics.attempts.filter((a: any) => a.percentage >= 70 && a.percentage < 80).length, color: 'bg-yellow-500' },
                  { range: '60-69%', count: statistics.attempts.filter((a: any) => a.percentage >= 60 && a.percentage < 70).length, color: 'bg-orange-500' },
                  { range: '0-59%', count: statistics.attempts.filter((a: any) => a.percentage < 60).length, color: 'bg-red-500' }
                ].map((item, index) => {
                  const percentage = statistics.totalAttempts > 0 ? (item.count / statistics.totalAttempts) * 100 : 0;
                  return (
                    <div key={index} className="flex items-center space-x-4">
                      <div className="w-16 text-sm text-gray-400">{item.range}</div>
                      <div className="flex-1 bg-gray-700 rounded-full h-6 relative overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                          className={`h-full ${item.color} rounded-full`}
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs text-white font-medium">
                            {item.count} ({Math.round(percentage)}%)
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tentatives récentes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-purple-400" />
                Tentatives Récentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {statistics.attempts.slice(0, 10).map((attempt: any, index: number) => (
                  <motion.div
                    key={attempt.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 + index * 0.05 }}
                    className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        attempt.passed ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {attempt.passed ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">
                          Tentative #{attempt.attempt_number}
                        </p>
                        <p className="text-gray-400 text-xs">
                          {new Date(attempt.completed_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Badge variant="outline" className={getScoreBadgeColor(attempt.percentage)}>
                        {Math.round(attempt.percentage)}%
                      </Badge>
                      <div className="text-right">
                        <p className="text-white text-sm">{attempt.score}/{attempt.max_score}</p>
                        <p className="text-gray-400 text-xs">{formatQuizTime(attempt.time_spent)}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Analyse des performances */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Award className="w-5 h-5 mr-2 text-yellow-400" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gray-700/30 rounded-lg">
                  <div className="text-2xl font-bold text-green-400 mb-1">
                    {statistics.passedAttempts}
                  </div>
                  <div className="text-sm text-gray-400">Tentatives Réussies</div>
                  <div className="mt-2">
                    <Progress value={statistics.passRate} className="h-2" />
                  </div>
                </div>

                <div className="text-center p-4 bg-gray-700/30 rounded-lg">
                  <div className="text-2xl font-bold text-blue-400 mb-1">
                    {Math.round(statistics.averageScore)}%
                  </div>
                  <div className="text-sm text-gray-400">Score Moyen</div>
                  <div className="mt-2">
                    <Progress value={statistics.averageScore} className="h-2" />
                  </div>
                </div>

                <div className="text-center p-4 bg-gray-700/30 rounded-lg">
                  <div className="text-2xl font-bold text-orange-400 mb-1">
                    {Math.round(statistics.averageTime / 60)}m
                  </div>
                  <div className="text-sm text-gray-400">Temps Moyen</div>
                  <div className="mt-2 text-xs text-gray-500">
                    {formatQuizTime(statistics.averageTime)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Analyse détaillée par question */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <div className="flex items-center">
                  <HelpCircle className="w-5 h-5 mr-2 text-cyan-400" />
                  Analyse par Question
                </div>
                {isLoadingQuestions && (
                  <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {questionStatistics.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                    <HelpCircle className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-gray-400 text-sm">
                    {isLoadingQuestions ? 'Chargement des statistiques par question...' : 'Aucune donnée disponible pour l\'analyse par question'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {questionStatistics.map((question, index) => {
                    const difficultyLevel = question.difficulty < 0.3 ? 'Facile' : question.difficulty < 0.7 ? 'Moyen' : 'Difficile';
                    const difficultyColor = question.difficulty < 0.3 ? 'text-green-400' : question.difficulty < 0.7 ? 'text-yellow-400' : 'text-red-400';
                    const successRate = Math.round(question.correctRate);
                    
                    return (
                      <motion.div
                        key={question.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.9 + index * 0.05 }}
                        className="bg-gray-700/30 rounded-lg p-4 border border-gray-600/30"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <Badge variant="outline" className="border-cyan-500/30 text-cyan-400 bg-cyan-500/10 text-xs">
                                Question {question.sort_order}
                              </Badge>
                              <Badge variant="outline" className="border-purple-500/30 text-purple-400 bg-purple-500/10 text-xs">
                                {question.points} pt{question.points > 1 ? 's' : ''}
                              </Badge>
                              <Badge variant="outline" className={`border-gray-500/30 bg-gray-500/10 text-xs ${difficultyColor}`}>
                                {difficultyLevel}
                              </Badge>
                            </div>
                            <h4 className="text-white text-sm font-medium leading-relaxed mb-3">
                              {question.question_text.length > 100 
                                ? `${question.question_text.substring(0, 100)}...` 
                                : question.question_text}
                            </h4>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                          <div className="bg-black/20 p-3 rounded-lg">
                            <div className="text-lg font-bold text-blue-400">{question.totalAnswers}</div>
                            <div className="text-xs text-gray-400">Réponses</div>
                          </div>
                          
                          <div className="bg-black/20 p-3 rounded-lg">
                            <div className={`text-lg font-bold ${getScoreColor(successRate)}`}>{successRate}%</div>
                            <div className="text-xs text-gray-400">Taux de réussite</div>
                            <div className="mt-1">
                              <Progress value={successRate} className="h-1" />
                            </div>
                          </div>
                          
                          <div className="bg-black/20 p-3 rounded-lg">
                            <div className="text-lg font-bold text-orange-400">{formatQuizTime(question.averageTime)}</div>
                            <div className="text-xs text-gray-400">Temps moyen</div>
                          </div>
                          
                          <div className="bg-black/20 p-3 rounded-lg">
                            <div className="text-lg font-bold text-green-400">{question.correctAnswers}</div>
                            <div className="text-xs text-gray-400">Bonnes réponses</div>
                          </div>
                        </div>
                        
                        {/* Indicateur de performance */}
                        <div className="mt-3 pt-3 border-t border-gray-600/30">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-400">
                              Performance: 
                              <span className={difficultyColor}>
                                {successRate >= 80 ? 'Excellente' : successRate >= 60 ? 'Bonne' : successRate >= 40 ? 'Moyenne' : 'À améliorer'}
                              </span>
                            </span>
                            {successRate < 50 && (
                              <Badge variant="outline" className="border-red-500/30 text-red-400 bg-red-500/10">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Attention requise
                              </Badge>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </ScrollArea>
  );
};

export { QuizStatistics };
